import {
  ArrowRightStartOnRectangleIcon,
  PencilIcon,
  PlusIcon,
  SquaresPlusIcon,
} from '@heroicons/react/24/solid';
import {
  CurrentStep,
  HtmlFinalStep,
  HtmlStepperStep,
} from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import { classNames } from '@superfunnel/utils';
import { Code, MoreVert, MultiplePagesEmpty, Trash } from 'iconoir-react';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import SortableList from '../SortableList';
import { useHtmlStepperEditor } from './HtmlStepperEditorContext';
import { StepEditPopover } from './StepEditPopover';
import { FinalStepThumbnail, StepThumbnail } from './StepThumbnail';

/**
 * Steps sidebar for the Quiz Editor - always visible in Editor/Preview modes.
 * Shows a list of steps with thumbnails and allows navigation/management.
 */
export const HtmlStepperEditorStepsSidebar = () => {
  const { state, data, actions, iframe } = useHtmlStepperEditor();
  const { currentStep, setCurrentStep } = state;
  const { formData } = data;
  const {
    addStep,
    addFinalStep,
    addWrapper,
    deleteStep,
    deleteFinalStep,
    deleteWrapper,
    duplicateStep,
    duplicateFinalStep,
    reorderSteps,
    reorderFinalSteps,
  } = actions;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Track which popover is currently open (only one at a time)
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  const steps = formData?.steps ?? [];
  const finalSteps = formData?.finalSteps ?? [];
  const hasWrapper = !!formData?.wrapper;

  // Scroll selected step into view when currentStep changes
  useEffect(() => {
    if (!currentStep) return;

    const stepId = currentStep.type === 'wrapper' ? 'wrapper' : currentStep.id;
    const stepElement = stepRefs.current.get(stepId ?? '');

    if (stepElement && scrollContainerRef.current) {
      stepElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [currentStep]);

  const handleStepClick = useCallback(
    (step: HtmlStepperStep) => {
      const newStep: CurrentStep = { type: 'step', id: step.id };
      setCurrentStep(newStep);
      iframe.goToStep(newStep);
    },
    [setCurrentStep, iframe],
  );

  const handleFinalStepClick = useCallback(
    (step: HtmlFinalStep) => {
      const newStep: CurrentStep = { type: 'finalStep', id: step.id };
      setCurrentStep(newStep);
      iframe.goToStep(newStep);
    },
    [setCurrentStep, iframe],
  );

  const handleWrapperClick = useCallback(() => {
    const newStep: CurrentStep = { type: 'wrapper' };
    setCurrentStep(newStep);
    iframe.goToStep(newStep);
  }, [setCurrentStep, iframe]);

  const isWrapperSelected = currentStep?.type === 'wrapper';
  const isStepSelected = (stepId: string) =>
    currentStep?.type === 'step' && currentStep.id === stepId;
  const isFinalStepSelected = (stepId: string) =>
    currentStep?.type === 'finalStep' && currentStep.id === stepId;

  // Register ref for a step element
  const registerStepRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el) {
        stepRefs.current.set(id, el);
      } else {
        stepRefs.current.delete(id);
      }
    },
    [],
  );

  return (
    <div className="ms-2 flex w-[260px] shrink-0 flex-col gap-3 rounded-t-[15px] bg-[#f2f4f7] p-3">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <p className="text-base-900 text-base font-medium">Steps</p>
      </div>

      {/* Add Step Button */}
      <AddStepDropdown
        onAddStep={addStep}
        onAddFinalStep={addFinalStep}
        onAddWrapper={addWrapper}
        hasWrapper={hasWrapper}
      />

      {/* Steps List */}
      <div
        ref={scrollContainerRef}
        className="flex flex-col gap-2 overflow-y-auto"
      >
        {/* Wrapper Item */}
        {hasWrapper && (
          <>
            <WrapperItem
              ref={registerStepRef('wrapper')}
              isSelected={isWrapperSelected}
              onClick={handleWrapperClick}
              onDelete={deleteWrapper}
              isPopoverOpen={openPopoverId === 'wrapper'}
              onPopoverToggle={() =>
                setOpenPopoverId((prev) =>
                  prev === 'wrapper' ? null : 'wrapper',
                )
              }
            />
            <Separator />
          </>
        )}

        {/* Regular Steps - Sortable */}
        <SortableList
          items={steps}
          dividerClassName="space-y-2"
          hideDragHandle
          itemClassName={(step) =>
            classNames(
              'w-full flex flex-col items-stretch gap-1 rounded-lg px-2.5 pt-1.5 pb-2 cursor-pointer transition-colors',
              isStepSelected(step.id)
                ? 'ring-1 ring-inset ring-[#b7a1ff] bg-[#faf9ff]'
                : 'bg-white',
            )
          }
          onItemClick={handleStepClick}
          renderItemContent={(step, index) => (
            <StepItemContent
              ref={registerStepRef(step.id)}
              step={step}
              index={index}
              isSelected={isStepSelected(step.id)}
              onDuplicate={() => duplicateStep(step.id)}
              onDelete={() => deleteStep(step.id)}
              isPopoverOpen={openPopoverId === `step-${step.id}`}
              onPopoverToggle={() =>
                setOpenPopoverId((prev) =>
                  prev === `step-${step.id}` ? null : `step-${step.id}`,
                )
              }
            />
          )}
          onChange={reorderSteps}
        />

        {steps.length === 0 && (
          <div className="text-base-400 py-3 text-center text-sm">
            No steps yet. Click the button above to add one.
          </div>
        )}

        <Separator />

        {finalSteps.length === 0 && (
          <div className="text-base-400 py-3 text-center text-sm">
            No final steps yet. Click the button above to add one.
          </div>
        )}

        {/* Final Steps - Sortable */}
        <SortableList
          items={finalSteps}
          dividerClassName="space-y-2"
          hideDragHandle
          itemClassName={(step) =>
            classNames(
              'flex flex-col gap-1 rounded-lg px-2.5 pt-1.5 pb-2 cursor-pointer transition-colors',
              isFinalStepSelected(step.id)
                ? 'ring-1 ring-inset ring-[#b7a1ff] bg-[#faf9ff]'
                : 'bg-white',
            )
          }
          onItemClick={handleFinalStepClick}
          renderItemContent={(step, index) => (
            <FinalStepItemContent
              ref={registerStepRef(step.id)}
              step={step}
              index={index}
              isSelected={isFinalStepSelected(step.id)}
              onDuplicate={() => duplicateFinalStep(step.id)}
              onDelete={() => deleteFinalStep(step.id)}
              isPopoverOpen={openPopoverId === `final-${step.id}`}
              onPopoverToggle={() =>
                setOpenPopoverId((prev) =>
                  prev === `final-${step.id}` ? null : `final-${step.id}`,
                )
              }
            />
          )}
          onChange={reorderFinalSteps}
        />
      </div>
    </div>
  );
};

// Add Step Dropdown Component
interface AddStepDropdownProps {
  onAddStep: () => void;
  onAddFinalStep: (type: 'redirect' | 'html') => void;
  onAddWrapper: () => void;
  hasWrapper: boolean;
}

const AddStepDropdown = ({
  onAddStep,
  onAddFinalStep,
  onAddWrapper,
  hasWrapper,
}: AddStepDropdownProps) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <Button
        variant="outline"
        color="white"
        className="h-8 w-full justify-center gap-2 border-[#e4e7ec] bg-white px-3 py-[7px] text-[13px] font-medium shadow-[0px_1px_2px_0_rgba(21,28,36,0.05)]"
      >
        <PlusIcon className="size-[18px]" />
        Add step
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="center"
      className="w-(--radix-dropdown-menu-trigger-width)"
    >
      <DropdownMenuItem onClick={onAddStep}>
        <Code className="size-4" />
        New Step
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => onAddFinalStep('html')}>
        <Code className="size-4" />
        Final Step (HTML)
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onAddFinalStep('redirect')}>
        <ArrowRightStartOnRectangleIcon className="size-4" />
        Final Step (Redirect)
      </DropdownMenuItem>
      {!hasWrapper && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onAddWrapper}>
            <SquaresPlusIcon className="size-4" />
            Add Wrapper
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

// Wrapper Item Component
interface WrapperItemProps {
  isSelected: boolean;
  onClick: () => void;
  onDelete: () => void;
  isPopoverOpen: boolean;
  onPopoverToggle: () => void;
}

const WrapperItem = forwardRef<HTMLDivElement, WrapperItemProps>(
  ({ isSelected, onClick, onDelete, isPopoverOpen, onPopoverToggle }, ref) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    return (
      <StepEditPopover
        type="wrapper"
        open={isPopoverOpen}
        onOpenChange={(open) => {
          if (!open) onPopoverToggle();
        }}
      >
        <div
          ref={ref}
          data-step-card
          className={classNames(
            'flex cursor-pointer flex-col gap-1 rounded-lg px-2.5 pt-1.5 pb-2 transition-colors',
            isSelected
              ? 'bg-[#faf9ff] ring-1 ring-[#b7a1ff] ring-inset'
              : 'bg-white',
          )}
          style={{ scrollMargin: '8px' }}
          onClick={onClick}
        >
          {/* Wrapper Header */}
          <div className="flex items-center gap-1">
            <div className="flex flex-1 items-start gap-1.5">
              <p
                className={classNames(
                  'shrink-0 text-xs font-medium',
                  isSelected ? 'text-base-900' : 'text-base-700',
                )}
              >
                Wrapper
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon-xs"
              className={isPopoverOpen ? 'bg-base-100' : ''}
              onClick={(e) => {
                e.stopPropagation();
                onPopoverToggle();
              }}
            >
              <PencilIcon className="text-base-400 size-3.5" />
            </Button>
            <DropdownMenu
              modal={false}
              open={isMenuOpen}
              onOpenChange={setIsMenuOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className={isMenuOpen ? 'bg-base-50' : ''}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVert className="text-base-500 size-4.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenuItem
                  className="text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <Trash className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </StepEditPopover>
    );
  },
);
WrapperItem.displayName = 'WrapperItem';

// Step Item Content Component (used inside SortableList)
interface StepItemContentProps {
  step: HtmlStepperStep;
  index: number;
  isSelected: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  isPopoverOpen: boolean;
  onPopoverToggle: () => void;
}

const StepItemContent = forwardRef<HTMLDivElement, StepItemContentProps>(
  (
    {
      step,
      index,
      isSelected,
      onDuplicate,
      onDelete,
      isPopoverOpen,
      onPopoverToggle,
    },
    ref,
  ) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    return (
      <StepEditPopover
        type="step"
        step={step}
        sideOffset={16}
        alignOffset={-8}
        open={isPopoverOpen}
        onOpenChange={(open) => {
          if (!open) onPopoverToggle();
        }}
      >
        <div
          ref={ref}
          data-step-card
          className="flex w-full flex-col gap-1"
          style={{ scrollMargin: '8px' }}
        >
          {/* Step Header */}
          <div className="flex items-center gap-1">
            <div className="flex flex-1 items-start gap-1.5">
              <p
                className={classNames(
                  'shrink-0 text-xs font-medium',
                  isSelected ? 'text-base-900' : 'text-base-700',
                )}
              >
                Step {index + 1}
              </p>
              <p
                className={classNames(
                  'line-clamp-1 text-xs',
                  isSelected ? 'text-base-700' : 'text-base-500',
                )}
              >
                {step.name || step.description || ''}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon-xs"
              className={isPopoverOpen ? 'bg-base-100' : ''}
              onClick={(e) => {
                e.stopPropagation();
                onPopoverToggle();
              }}
            >
              <PencilIcon className="text-base-400 size-3.5" />
            </Button>
            <StepActionsMenu
              isOpen={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          </div>

          {/* Step Thumbnail */}
          <StepThumbnail stepId={step.id} />
        </div>
      </StepEditPopover>
    );
  },
);
StepItemContent.displayName = 'StepItemContent';

// Final Step Item Content Component (used inside SortableList)
interface FinalStepItemContentProps {
  step: HtmlFinalStep;
  index: number;
  isSelected: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  isPopoverOpen: boolean;
  onPopoverToggle: () => void;
}

const FinalStepItemContent = forwardRef<
  HTMLDivElement,
  FinalStepItemContentProps
>(
  (
    {
      step,
      index,
      isSelected,
      onDuplicate,
      onDelete,
      isPopoverOpen,
      onPopoverToggle,
    },
    ref,
  ) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    return (
      <StepEditPopover
        type="finalStep"
        finalStep={step}
        sideOffset={16}
        alignOffset={-8}
        open={isPopoverOpen}
        onOpenChange={(open) => {
          if (!open) onPopoverToggle();
        }}
      >
        <div
          ref={ref}
          data-step-card
          className="flex w-full flex-col gap-1"
          style={{ scrollMargin: '8px' }}
        >
          {/* Step Header */}
          <div className="flex items-center gap-1">
            <div className="flex flex-1 items-start gap-1.5">
              <p
                className={classNames(
                  'shrink-0 text-xs font-medium',
                  isSelected ? 'text-base-900' : 'text-base-700',
                )}
              >
                Final Step {index + 1}
              </p>
              <p
                className={classNames(
                  'line-clamp-1 flex-1 text-xs',
                  isSelected ? 'text-base-700' : 'text-base-500',
                )}
              >
                {step.name || step.description || ''}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon-xs"
              className={isPopoverOpen ? 'bg-base-100' : ''}
              onClick={(e) => {
                e.stopPropagation();
                onPopoverToggle();
              }}
            >
              <PencilIcon className="text-base-400 size-3.5" />
            </Button>
            <FinalStepActionsMenu
              isOpen={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          </div>

          {/* Step Thumbnail */}
          <FinalStepThumbnail step={step} />
        </div>
      </StepEditPopover>
    );
  },
);
FinalStepItemContent.displayName = 'FinalStepItemContent';

// Step Actions Menu
interface StepActionsMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const StepActionsMenu = ({
  isOpen,
  onOpenChange,
  onDuplicate,
  onDelete,
}: StepActionsMenuProps) => (
  <DropdownMenu modal={false} open={isOpen} onOpenChange={onOpenChange}>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="icon-xs"
        className={classNames(isOpen ? 'bg-base-100' : '')}
        onClick={(e) => e.stopPropagation()}
      >
        <MoreVert className="text-base-500 size-4.5" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          onDuplicate();
        }}
      >
        <MultiplePagesEmpty className="size-4" />
        Duplicate
      </DropdownMenuItem>
      <DropdownMenuItem
        className="text-red-500"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <Trash className="size-5" />
        Delete
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

// Final Step Actions Menu
interface FinalStepActionsMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const FinalStepActionsMenu = ({
  isOpen,
  onOpenChange,
  onDuplicate,
  onDelete,
}: FinalStepActionsMenuProps) => (
  <DropdownMenu modal={false} open={isOpen} onOpenChange={onOpenChange}>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="icon-xs"
        className={isOpen ? 'bg-base-100' : ''}
        onClick={(e) => e.stopPropagation()}
      >
        <MoreVert className="text-base-500 size-4.5" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          onDuplicate();
        }}
      >
        <MultiplePagesEmpty className="size-4" />
        Duplicate
      </DropdownMenuItem>
      <DropdownMenuItem
        className="text-red-500"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <Trash className="size-5" />
        Delete
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
